/**
 * Pure document operations.
 *
 * Every function here returns a new document; nothing mutates. That is what makes
 * undo/redo a stack of references rather than a replay of inverse operations, and
 * it is why `canMove` can answer "would this break anything?" by simply building
 * the graph of a document that does not exist yet.
 *
 * The move guard is the interesting one. Because evaluation is strictly in document
 * order (graph.ts), dragging a cell can change what it means -- and a spreadsheet's
 * answer, letting you break a formula and discover it later, is the thing sheets
 * exist to improve on. So a move that would leave a symbol undefined is refused,
 * and the refusal names the cells that would have to come along.
 */
import { buildGraph, downstreamOf, upstreamOf } from './graph'
import type { Cell, CellId, SheetDoc } from './types'

export function newCellId(): CellId {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // jsdom without webcrypto, and very old browsers. Uniqueness within one document
  // is all that is required of an id here.
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

export function indexOfCell(doc: SheetDoc, id: CellId): number {
  return doc.cells.findIndex((cell) => cell.id === id)
}

export function insertCell(doc: SheetDoc, cell: Cell, at: number): SheetDoc {
  const clamped = Math.max(0, Math.min(at, doc.cells.length))
  const cells = [...doc.cells]
  cells.splice(clamped, 0, cell)
  return { ...doc, cells }
}

export function removeCell(doc: SheetDoc, id: CellId): SheetDoc {
  return { ...doc, cells: doc.cells.filter((cell) => cell.id !== id) }
}

export function replaceCell(doc: SheetDoc, cell: Cell): SheetDoc {
  return { ...doc, cells: doc.cells.map((c) => (c.id === cell.id ? cell : c)) }
}

/** Moves `id` so it lands at `to` in the resulting array. No legality check. */
export function moveCellUnchecked(doc: SheetDoc, id: CellId, to: number): SheetDoc {
  const from = indexOfCell(doc, id)
  if (from < 0) return doc
  const cells = [...doc.cells]
  const [moved] = cells.splice(from, 1)
  if (moved === undefined) return doc
  cells.splice(Math.max(0, Math.min(to, cells.length)), 0, moved)
  return { ...doc, cells }
}

export interface MoveCheck {
  readonly ok: boolean
  /** Cells that would newly fail, in document order. */
  readonly breaks: readonly CellId[]
  /** Symbols that would become undefined, for the message. */
  readonly symbols: readonly string[]
  /** Cells that would have to move too for the drop to be legal. */
  readonly bringAlong: readonly CellId[]
}

const OK: MoveCheck = { ok: true, breaks: [], symbols: [], bringAlong: [] }

/**
 * Would moving `id` to `to` leave anything undefined? Answered by building the
 * graph of the hypothetical document and comparing the set of cells in error --
 * cells already broken before the move do not count against it.
 */
export function canMove(doc: SheetDoc, id: CellId, to: number): MoveCheck {
  const from = indexOfCell(doc, id)
  if (from < 0 || from === to) return OK

  const before = buildGraph(doc.cells)
  const after = buildGraph(moveCellUnchecked(doc, id, to).cells)

  const breaks: CellId[] = []
  const symbols: string[] = []

  for (const cellId of after.order) {
    const err = after.errors.get(cellId)
    if (err?.kind !== 'undefined') continue
    if (before.errors.get(cellId)?.kind === 'undefined') continue
    breaks.push(cellId)
    const missing = err.params?.symbol
    if (typeof missing === 'string') {
      for (const sym of missing.split(', ')) {
        if (sym && !symbols.includes(sym)) symbols.push(sym)
      }
    }
  }

  if (breaks.length === 0) return OK

  // What would have to come along. Transitive, because a dependency has dependencies
  // of its own -- carrying only the immediate one lands a group that is still broken.
  //
  // Moving up, the cell outruns what it reads, so its upstream comes with it.
  // Moving down, it outruns what reads it, so its downstream does.
  const related = to < from ? upstreamOf(before, [id]) : downstreamOf(before, [id])
  const lo = Math.min(to, from)
  const hi = Math.max(to, from)

  const bringAlong = doc.cells
    .filter((cell, index) => {
      if (cell.id === id || !related.has(cell.id)) return false
      return to < from ? index >= lo : index <= hi
    })
    .map((cell) => cell.id)

  return { ok: false, breaks, symbols, bringAlong }
}

/** Moves only when legal. Returns the original document otherwise. */
export function moveCell(doc: SheetDoc, id: CellId, to: number): SheetDoc {
  return canMove(doc, id, to).ok ? moveCellUnchecked(doc, id, to) : doc
}

/**
 * Moves `id` to `to` together with the cells it needs, keeping their relative order
 * and placing them immediately above. This is the "bring it along" button: usually
 * what was meant, and one gesture rather than several drags.
 */
export function moveCellWithDependencies(doc: SheetDoc, id: CellId, to: number): SheetDoc {
  const check = canMove(doc, id, to)
  if (check.ok) return moveCellUnchecked(doc, id, to)

  const groupSet = new Set([...check.bringAlong, id])
  const rest = doc.cells.filter((cell) => !groupSet.has(cell.id))
  // Keep the group in its original relative order rather than the order the
  // dependencies happened to be discovered in.
  const ordered = doc.cells.filter((cell) => groupSet.has(cell.id))

  // `to` indexes the original array; translate it to the thinned one.
  const removedBefore = doc.cells.filter(
    (cell, index) => groupSet.has(cell.id) && index < to,
  ).length
  const at = Math.max(0, Math.min(to - removedBefore, rest.length))

  const cells = [...rest]
  cells.splice(at, 0, ...ordered)
  const next: SheetDoc = { ...doc, cells }

  // The closure should make this legal by construction. Verify rather than assume:
  // handing back a document that fails to evaluate is worse than refusing the drag.
  const before = buildGraph(doc.cells)
  const after = buildGraph(next.cells)
  for (const cellId of after.order) {
    if (
      after.errors.get(cellId)?.kind === 'undefined' &&
      before.errors.get(cellId)?.kind !== 'undefined'
    ) {
      return doc
    }
  }

  return next
}
